/**
 * Metric plan — how a cross-workspace card COMBINES metrics into its columns.
 *
 * By default a combined card matches metrics across workspaces BY NAME: a column
 * appears only when every chosen workspace tracks a metric with the same title
 * (so "AVG Ticket" from Sales lines up with "AVG Ticket" from In-House Sales).
 * That is the auto plan, and it is what every board did before this file existed.
 *
 * An admin can override it with an explicit plan, stored on the card's settings
 * as `metricPlan`. Each column then names, PER WORKSPACE, which metric feeds it —
 * so two differently-named metrics ("AVG Ticket" ↔ "Average Ticket") can be
 * paired into one column — and whether the workspaces are AVERAGED or SUMMED into
 * the shown number. A column can also be switched off.
 *
 * Shape (all fields optional except sources):
 *   {
 *     on: true,
 *     cols: [
 *       { key, label, kind: "$"|"%"|"#", mode: "avg"|"sum", show: true,
 *         sources: { [workspaceId]: kpiId },
 *         sourceWindows: { [workspaceId]: { window, customRange? } } }
 *     ]
 *   }
 *
 * `sourceWindows` is optional per workspace source. `window: "follow"` (or absent)
 * means use the card / board date filter for that source.
 *
 * Everything here is pure — no React, no fetching — so the editor's preview and
 * the live card agree by construction, exactly like payoutCurve.js.
 */

export const mpNameKey = (k) => (k?.title || k?.name || "").trim().toLowerCase();

/** Resolve $ / % / # — Closed Rate sometimes lacks kind on the GroupKpi doc. */
export const mpKindOf = (k) => {
  if (k?.kind === "$" || k?.kind === "%") return k.kind;
  const t = mpNameKey(k);
  if (/\bclose[d]?\s*rate\b|\bconversion\b|\bwin\s*rate\b/.test(t)) return "%";
  return "#";
};

/** A stable, url-safe-ish key from a label (falls back to a random-ish token). */
export const mpKeyFromLabel = (label) => {
  const base = (label || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  return base || `col-${Math.random().toString(36).slice(2, 8)}`;
};

const dayStr = (v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : "");

/** Sanitize one source's date window. */
export const sanitizeSourceWindow = (w) => {
  if (!w || typeof w !== "object") return null;
  const allowed = ["follow", "today", "yesterday", "week", "lastWeek", "lastMonth", "month", "quarter", "ytd", "year", "custom"];
  const window = allowed.includes(w.window) ? w.window : "follow";
  if (window === "follow") return { window: "follow" };
  if (window !== "custom") return { window };
  const from = dayStr(w.customRange?.from);
  const to = dayStr(w.customRange?.to);
  if (!from && !to) return { window: "follow" };
  return { window: "custom", customRange: { ...(from ? { from } : {}), ...(to ? { to } : {}) } };
};

/** Legacy auto-pair placeholder — not a real KPI id and breaks activity lookups. */
const LEGACY_PLAN_KEYS = new Set(["paired", ""]);

/** Does this settings blob carry a real, on plan? */
export function hasMetricPlan(plan) {
  return !!(plan && plan.on && Array.isArray(plan.cols) && plan.cols.length);
}

/** Resolved window for one workspace source inside a column. */
export const sourceWindowOf = (col, wsId, boardWin = "month", boardCustom = null) => {
  const raw = col?.sourceWindows?.[String(wsId)];
  if (!raw || !raw.window || raw.window === "follow") {
    return { window: boardWin || "month", custom: boardCustom };
  }
  if (raw.window === "custom") {
    return { window: "custom", custom: raw.customRange || null };
  }
  return { window: raw.window, custom: null };
};

/** Metrics rows for a saved custom card — keeps plan column keys in sync. */
export const metricsFromPlan = (plan) => {
  if (!hasMetricPlan(plan)) return null;
  const cols = (plan.cols || []).filter((c) => c && c.show !== false && c.key && !LEGACY_PLAN_KEYS.has(String(c.key)));
  if (!cols.length) return null;
  return cols.map((c) => ({ metricId: String(c.key), graph: "number" }));
};

/** Rank windows so the broadest fetch wins (year ⊃ quarter ⊃ month ⊃ week ⊃ day). */
const WINDOW_RANK = {
  custom: 90, ytd: 80, year: 80, quarter: 70, month: 60, lastMonth: 55, lastWeek: 50, week: 40, yesterday: 20, today: 10,
};

/** When a metric plan pins sources to their own dates, widen the card fetch window. */
export const broadestPlanWindow = (plan, boardWin = "month", boardCustom = null) => {
  if (!hasMetricPlan(plan)) return { window: boardWin, custom: boardCustom };
  let bestWin = boardWin || "month";
  let bestCustom = boardCustom;
  let bestRank = WINDOW_RANK[bestWin === "year" ? "ytd" : bestWin] || 0;
  (plan.cols || []).forEach((col) => {
    Object.keys(col?.sources || {}).forEach((wsId) => {
      const sw = sourceWindowOf(col, wsId, boardWin, boardCustom);
      const win = sw.window === "year" ? "ytd" : sw.window;
      const rank = WINDOW_RANK[win] || 0;
      if (rank > bestRank) {
        bestRank = rank;
        bestWin = win;
        bestCustom = sw.custom;
      }
    });
  });
  return { window: bestWin, custom: bestCustom };
};

/**
 * Auto columns: the metrics the workspaces have IN COMMON, matched by name.
 * @param {{id:string, defs:Array}[]} perWs  each workspace's metric defs
 * @param {boolean} liveOnly  match only the LIVE (≤5 badge) metrics vs all
 */
export const autoMetricColumns = (perWs, liveOnly = true) => {
  const first = perWs[0];
  if (!first) return [];
  const listOf = (w) => (liveOnly ? w.defs : (w.defsAll || w.defs)) || [];
  return listOf(first)
    .filter((k) => perWs.every((w) => listOf(w).some((x) => mpNameKey(x) === mpNameKey(k))))
    .map((k) => {
      const key = mpNameKey(k);
      const sources = {};
      perWs.forEach((w) => {
        const hit = listOf(w).find((x) => mpNameKey(x) === key);
        if (hit) sources[String(w.id)] = String(hit._id);
      });
      return {
        key,
        label: (k.title || k.name || "").trim(),
        kind: mpKindOf(k),
        mode: "avg",
        show: true,
        sources,
      };
    });
};

/**
 * The columns a card should actually render: the stored plan when one is set,
 * otherwise the auto (name-matched) columns. Plan columns are resolved against
 * the CURRENT workspaces — a source pointing at a retired metric simply
 * contributes nothing, it never breaks the column.
 */
export const effectiveMetricColumns = (perWs, plan, liveOnly = true) => {
  if (hasMetricPlan(plan)) {
    return plan.cols
      .filter((c) => c && c.show !== false)
      .map((c) => {
        let kind = c.kind === "$" || c.kind === "%" ? c.kind : "#";
        /* Prefer the real KPI's kind over a mis-tagged plan unit (Revenue as %). */
        const sources = c.sources && typeof c.sources === "object" ? c.sources : {};
        Object.entries(sources).some(([wsId, kpiId]) => {
          const w = (perWs || []).find((x) => String(x.id) === String(wsId));
          if (!w) return false;
          const list = [...(w.defsAll || []), ...(w.defs || [])];
          const def = list.find((k) => k && String(k._id) === String(kpiId));
          if (!def) return false;
          const dk = mpKindOf(def);
          if (dk === "$" || dk === "%") {
            kind = dk;
            return true;
          }
          return false;
        });
        if (kind === "#") kind = mpKindOf({ title: c.label, kind: c.kind });
        const sourceWindows = {};
        if (c.sourceWindows && typeof c.sourceWindows === "object") {
          Object.entries(c.sourceWindows).forEach(([ws, sw]) => {
            const clean = sanitizeSourceWindow(sw);
            if (clean && clean.window !== "follow") sourceWindows[String(ws)] = clean;
          });
        }
        const label = (c.label || "").trim() || "Metric";
        let key = c.key || mpKeyFromLabel(label);
        if (LEGACY_PLAN_KEYS.has(String(key)) && label && label !== "Metric") {
          key = mpKeyFromLabel(label);
        }
        return {
          key,
          label,
          kind,
          mode: c.mode === "sum" ? "sum" : "avg",
          show: true,
          sources,
          ...(Object.keys(sourceWindows).length ? { sourceWindows } : {}),
        };
      });
  }
  return autoMetricColumns(perWs, liveOnly);
};

/**
 * Pool one column across the workspaces in scope.
 * @param {{amt:number, present:boolean}[]} cells  one per workspace
 * @param {"avg"|"sum"} mode                        across-workspace pooling
 * Only workspaces that actually carry the column count toward an average, so a
 * workspace that doesn't track the paired metric never drags the number to zero.
 */
export const poolColumn = (cells, mode) => {
  /* Averages skip empty/zero cells so an unscored workspace never drags a
     Closed Rate column to ~0. Sums keep zeros (they don't change the total). */
  const live = (cells || []).filter((c) => {
    if (!c || !c.present) return false;
    if (mode === "sum") return true;
    return (Number(c.amt) || 0) > 0;
  });
  if (!live.length) return 0;
  const sum = live.reduce((a, c) => a + (Number(c.amt) || 0), 0);
  return mode === "sum" ? sum : sum / live.length;
};

/**
 * Normalise a plan for saving — drop empty columns and junk, cap the size, and
 * keep only the fields we understand. Mirrored on the server sanitizer.
 */
export const sanitizeMetricPlan = (plan) => {
  if (!plan || typeof plan !== "object") return null;
  const cols = (Array.isArray(plan.cols) ? plan.cols : [])
    .map((c) => {
      const sources = {};
      if (c && c.sources && typeof c.sources === "object") {
        Object.entries(c.sources).forEach(([ws, kpi]) => {
          if (ws && kpi) sources[String(ws)] = String(kpi);
        });
      }
      const sourceWindows = {};
      if (c && c.sourceWindows && typeof c.sourceWindows === "object") {
        Object.entries(c.sourceWindows).forEach(([ws, sw]) => {
          const clean = sanitizeSourceWindow(sw);
          if (clean && clean.window !== "follow") sourceWindows[String(ws)] = clean;
        });
      }
      const label = (c && c.label ? String(c.label) : "").slice(0, 60);
      let key = (c && c.key ? String(c.key) : mpKeyFromLabel(label)).slice(0, 60);
      if (LEGACY_PLAN_KEYS.has(key) && label) key = mpKeyFromLabel(label).slice(0, 60);
      return {
        key,
        label,
        kind: c && (c.kind === "$" || c.kind === "%") ? c.kind : "#",
        mode: c && c.mode === "sum" ? "sum" : "avg",
        show: !(c && c.show === false),
        sources,
        ...(Object.keys(sourceWindows).length ? { sourceWindows } : {}),
      };
    })
    .filter((c) => Object.keys(c.sources).length > 0 || c.label)
    .slice(0, 24);
  if (!cols.length) return null;
  return { on: plan.on !== false, cols };
};
